import { useState } from "react";
import type { SignInOptions } from "@openmarketsai/app-sdk";
import { signIn as omSignIn, useAuthStatus } from "../lib/omauth.js";

/**
 * "Sign in with OpenMarkets" (OAuth 2.1 + PKCE via the SDK), as a reusable hook + button.
 *
 * `connect()` awaits `signIn()`, which opens a popup, keeps the app alive underneath, and
 * RESOLVES with the outcome — success stores the session (omauth flips `useIsAuthed`, and every
 * gated view unlocks in place), a cancel/closed window is `denied` (not an error), and a real
 * failure carries its message. The SDK handles all window coordination and falls back to a
 * full redirect if the popup is blocked.
 *
 * Used by the top bar, the mobile drawer, every LockedCard and the trade ticket — one sign-in
 * implementation, no duplicates.
 */
export function useSignIn() {
  const { loading, error } = useAuthStatus();
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const connect = async (opts?: SignInOptions) => {
    setBusy(true);
    setDenied(false);
    setLocalError(null);
    try {
      const r = await omSignIn(opts);
      if (r.status === "denied") setDenied(true);
      else if (r.status === "error") setLocalError(r.message);
    } finally {
      setBusy(false);
    }
  };

  return { connect, busy: busy || loading, denied, error: localError ?? error };
}

/** Friendly copy for a sign-in failure (the orphaned-magic-link case gets its own words). */
export function signInErrorText(error: string): string {
  return /orphan|no pending|no sign-in|not started|pending_authorization/i.test(error)
    ? "That sign-in link opened in a new tab, so it couldn’t finish here. Click Sign in to start over in this window."
    : `${error} — please try again.`;
}

type Size = "sm" | "md" | "lg";
const SIZES: Record<Size, string> = {
  sm: "px-2.5 py-1 text-[11px]",
  md: "min-h-11 px-3.5 text-xs lg:min-h-0 lg:py-1.5",
  lg: "w-full px-3 py-2.5 text-[14px]",
};

/**
 * The sign-in button. `feedback` shows the denied/error line under it (off for the compact
 * top-bar variant, where there's no room — the cards carry it instead).
 */
export function SignInButton({
  label = "Sign in",
  size = "md",
  feedback = false,
  opts,
  className = "",
}: {
  label?: string;
  size?: Size;
  feedback?: boolean;
  opts?: SignInOptions;
  className?: string;
}) {
  const { connect, busy, denied, error } = useSignIn();
  return (
    <div className={size === "lg" ? "w-full" : "inline-flex flex-col items-start"}>
      <button
        onClick={() => void connect(opts)}
        disabled={busy}
        className={`flex items-center justify-center gap-2 rounded-md bg-up/20 font-semibold text-up hover:bg-up/30 disabled:cursor-not-allowed disabled:opacity-60 ${SIZES[size]} ${className}`}
      >
        {busy ? (
          <>
            <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-up/50 border-t-transparent" />
            {size === "sm" ? "Signing in…" : "Waiting for OpenMarkets…"}
          </>
        ) : (
          label
        )}
      </button>
      {feedback && denied && (
        <p className="mt-2 text-[12px] text-dim">Sign-in cancelled. Click again whenever you’re ready.</p>
      )}
      {feedback && error && <p className="mt-2 text-[12px] text-down">{signInErrorText(error)}</p>}
    </div>
  );
}

/** Full-page spinner while a sign-in callback completes in THIS window (redirect return / popup leg). */
export function AuthSplash({ label = "Signing you in…" }: { label?: string }) {
  return (
    <div className="flex h-full items-center justify-center bg-bg text-ink">
      <div className="flex items-center gap-3 text-sm text-dim">
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-faint border-t-transparent" />
        {label}
      </div>
    </div>
  );
}
